import React from 'react';
import { StyleSheet, Text, View } from '@react-pdf/renderer';

import { formatCurrency } from '~libs/dataUtils';

import { ServiceTotal } from '~support-at-home/models';

interface ComponentProps {
  category_id: string;
  section?: any;
}

const styles = StyleSheet.create({
  container: {
    width: '100%',
    minWidth: 160,
    fontSize: 8,
    marginLeft: 'auto',
    marginTop: 4,
    marginBottom: 12,
    // No border/background, so it's outside the section visually
  },
  row: {
    flexDirection: 'row',
    paddingVertical: 2,
    paddingHorizontal: 2,
  },
  headerRow: {
    borderBottom: '1px solid #949494',
    paddingBottom: 2,
    marginBottom: 2,
  },
  totalRow: {
    borderTop: '1px solid #949494',
    paddingTop: 2,
    marginTop: 2,
  },
  serviceColumn: {
    width: '35%',
    fontSize: 8,
    paddingRight: 4,
    textAlign: 'left',
  },
  contribColumn: {
    width: '25%',
    fontSize: 8,
    textAlign: 'right',
  },
  governmentColumn: {
    width: '25%',
    fontSize: 8,
    textAlign: 'right',
  },
  totalColumn: {
    width: '15%',
    fontSize: 8,
    textAlign: 'right',
  },
  headerText: {
    fontWeight: 'bold',
  },
  totalText: {
    fontWeight: 'bold',
  },
  currencyValue: {
    paddingHorizontal: 4,
    paddingVertical: 1,
    backgroundColor: '#f8f9fa',
    border: '1px solid #ced4da',
    borderRadius: 2,
    fontSize: 8,
    textAlign: 'right',
  },
});

const SectionTotal: React.FC<ComponentProps> = ({ category_id, section }) => {
  const { service_totals, totals, items } = section || {};

  const serviceNameLookup = (items || []).reduce((acc: Record<string, string>, item: any) => {
    if (item.service_type && item.service_type_id) {
      acc[item.service_type_id] = item.service_type.name;
    }
    return acc;
  }, {});

  const getServiceName = (service_type_id: string | number): string => {
    return serviceNameLookup[service_type_id] || String(service_type_id);
  };

  const buildRow = (serviceTotal: ServiceTotal, index: number) => {
    const { service_type_id, contribution, govSubsidy, total } = serviceTotal;

    return (
      <View key={`row-${category_id}-${index}`} style={styles.row}>
        <Text style={styles.serviceColumn}>{getServiceName(service_type_id)}</Text>
        <Text style={styles.contribColumn}>
          <Text style={styles.currencyValue}>{formatCurrency(contribution, 0)}</Text>
        </Text>
        <Text style={styles.governmentColumn}>
          <Text style={styles.currencyValue}>{formatCurrency(govSubsidy, 0)}</Text>
        </Text>
        <Text style={styles.totalColumn}>
          <Text style={styles.currencyValue}>{formatCurrency(total, 0)}</Text>
        </Text>
      </View>
    );
  };

  const { cost, contribution, govSubsidy } = totals || {};

  if (!section || !service_totals) {
    return null;
  }

  return (
    <View style={styles.container}>
      <View style={[styles.row, styles.headerRow]}>
        <Text style={[styles.serviceColumn, styles.headerText]}>Service</Text>
        <Text style={[styles.contribColumn, styles.headerText]}>Co-Contribution</Text>
        <Text style={[styles.governmentColumn, styles.headerText]}>Gov. Contribution</Text>
        <Text style={[styles.totalColumn, styles.headerText]}>Total</Text>
      </View>
      {service_totals.map((serviceTotal: ServiceTotal, index: number) => buildRow(serviceTotal, index))}
      <View style={[styles.row, styles.totalRow]}>
        <Text style={styles.serviceColumn}></Text>
        <Text style={styles.contribColumn}>
          <Text style={[styles.currencyValue, styles.totalText]}>{formatCurrency(contribution, 0)}</Text>
        </Text>
        <Text style={styles.governmentColumn}>
          <Text style={[styles.currencyValue, styles.totalText]}>{formatCurrency(govSubsidy, 0)}</Text>
        </Text>
        <Text style={styles.totalColumn}>
          <Text style={[styles.currencyValue, styles.totalText]}>{formatCurrency(cost, 0)}</Text>
        </Text>
      </View>
    </View>
  );
};

export default SectionTotal;
